/**
 * NotificationDialog — کامپوننت نمایش پیام‌های سیستمی
 *
 * این کامپوننت به صورت خودکار در App.tsx ثبت شده است.
 * برای استفاده از hook useNotification() استفاده کنید — مستقیماً import نکنید.
 *
 * برای جزئیات: docs/notification-system.md
 */
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNotification } from '../contexts/NotificationContext';
import type { NotificationType } from '../contexts/NotificationContext';

// ─── Config ───────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<
  NotificationType,
  { icon: React.ReactNode; color: string; confirmColor: 'success' | 'error' | 'warning' | 'info' | 'primary' }
> = {
  success: {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32' }} />,
    color: '#2e7d32',
    confirmColor: 'success',
  },
  error: {
    icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: '#c62828' }} />,
    color: '#c62828',
    confirmColor: 'error',
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 64, color: '#e65100' }} />,
    color: '#e65100',
    confirmColor: 'warning',
  },
  info: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 64, color: '#0277bd' }} />,
    color: '#0277bd',
    confirmColor: 'info',
  },
  confirm: {
    icon: <HelpOutlineIcon sx={{ fontSize: 64, color: '#1565c0' }} />,
    color: '#1565c0',
    confirmColor: 'primary',
  },
  loading: {
    icon: <CircularProgress size={64} thickness={4} />,
    color: '#616161',
    confirmColor: 'primary',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const NotificationDialog: React.FC = () => {
  const { state, close } = useNotification();
  const { open, type, title, message, messages, confirmText, cancelText, onConfirm, onCancel } = state;
  const cfg = TYPE_CONFIG[type];
  const isLoading = type === 'loading';
  const isConfirm = type === 'confirm';

  const handleConfirm = () => {
    close();
    onConfirm?.();
  };

  const handleCancel = () => {
    close();
    onCancel?.();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : (isConfirm ? handleCancel : close)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, direction: 'rtl' } }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pt: 4,
          pb: 0,
          fontWeight: 700,
          color: cfg.color,
          fontSize: '1.1rem',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          {cfg.icon}
          <span>{title}</span>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
        {messages.length > 0 ? (
          <Box sx={{ textAlign: 'right', direction: 'rtl' }}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: idx < messages.length - 1 ? 1 : 0,
                  px: 1,
                }}
              >
                <Typography
                  component="span"
                  sx={{ color: cfg.color, fontWeight: 700, fontSize: '1rem', lineHeight: 1.6, flexShrink: 0 }}
                >
                  •
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                  {msg}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            {message}
          </Typography>
        )}
      </DialogContent>

      {!isLoading && (
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3, px: 3 }}>
          {isConfirm && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
              sx={{ minWidth: 110 }}
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant="contained"
            color={cfg.confirmColor}
            onClick={handleConfirm}
            sx={{ minWidth: 110 }}
            autoFocus
          >
            {isConfirm ? confirmText : 'متوجه شدم'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NotificationDialog;
